import { Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import { Button } from '../ui/Button';
import { IconCheck, IconChecklist } from '../icons';
import { ErrorState, StaleNotice, refreshFailed, shownState } from '../ui/QueryStates';

/**
 * First-run checklist (PRD-v1 §4.7). The server has computed this from real data since M6
 * — nothing here is a stored flag a shop could tick without doing the thing — and no
 * screen ever rendered it, so a new shop got no runway at all.
 *
 * It DISAPPEARS once the required steps are handled. A permanent checklist on the home
 * screen of a shop that finished setting up six months ago is nagging, not onboarding.
 */

/** Where each step actually gets done. A checklist that can't be acted on is a to-do list. */
const GO: Record<string, { to: string; label: string }> = {
  set_timezone: { to: '/settings', label: 'Settings' },
  import_customers: { to: '/import', label: 'Import' },
  add_first_job: { to: '/jobs', label: 'Jobs' },
  invite_team: { to: '/settings', label: 'Settings' },
  connect_qbo: { to: '/money', label: 'Money' },
  connect_stripe: { to: '/settings', label: 'Settings' },
};

export function GettingStartedCard() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = useQuery(trpc.org.onboarding.queryOptions());
  const skip = useMutation(
    trpc.org.skipOnboardingStep.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  /**
   * Quiet while loading and gone once finished — this is the one card that should stop
   * existing rather than sit there congratulating a shop that got on with its work.
   *
   * 🔴 But `q.isError` was in that same list, so a FAILED query rendered exactly what a
   * finished setup renders: nothing. A shop three steps into setting up opened Today, the
   * onboarding query failed, and the checklist they were working through had silently
   * vanished — indistinguishable from having completed it. Absence is a strong claim when
   * the card's whole job is to say what is left.
   *
   * An error keeps the card and says so. Offline does NOT: a paused query means the answer
   * is coming, the steps are unchanged underneath, and a warning about a checklist is not
   * worth a line on a field-facing screen with no connection.
   */
  const state = shownState(q, q.data);
  if (state === 'error') {
    return (
      <SectionCard title="Getting set up">
        <ErrorState
          icon={IconChecklist}
          what="your setup checklist"
          onRetry={() => void q.refetch()}
        />
      </SectionCard>
    );
  }
  if (state !== 'ready' || !q.data || q.data.complete) return null;

  const { steps, doneCount, requiredCount } = q.data;

  return (
    <SectionCard title={`Getting set up — ${doneCount} of ${requiredCount}`}>
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      <p className="mb-2 text-sm text-muted-foreground">
        This disappears on its own once you're through it. Anything you don't need, skip.
      </p>
      <ul className="space-y-1.5">
        {steps.map((s) => {
          const go = GO[s.key];
          const settled = s.done || s.skipped;
          return (
            <li
              key={s.key}
              className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-border p-2.5"
            >
              <span
                className={`flex size-5 shrink-0 items-center justify-center rounded-full border ${
                  s.done
                    ? 'border-success bg-success/10 text-success'
                    : 'border-border text-muted-foreground'
                }`}
              >
                {s.done ? <IconCheck size={12} /> : null}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block text-sm font-medium ${settled ? 'text-muted-foreground line-through' : ''}`}
                >
                  {s.label}
                  {/* Optional extras are labelled rather than hidden — a shop with no
                      QuickBooks should see that it is optional, not wonder what it missed. */}
                  {s.gated ? (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      optional
                    </span>
                  ) : null}
                </span>
                {s.hint && !settled ? (
                  <span className="block text-xs text-muted-foreground">{s.hint}</span>
                ) : null}
                {s.skipped ? (
                  <span className="block text-xs text-muted-foreground">Skipped.</span>
                ) : null}
              </span>
              {!settled && go ? (
                <Link
                  to={go.to}
                  className="shrink-0 text-sm font-semibold text-[var(--sw-brand-text)] hover:underline"
                >
                  {go.label}
                </Link>
              ) : null}
              {!settled && s.skippable ? (
                <Button
                  variant="ghost"
                  disabled={skip.isPending}
                  onClick={() =>
                    skip.mutate({
                      step: s.key as 'import_customers' | 'add_first_job' | 'invite_team',
                    })
                  }
                >
                  Skip
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
